// Delivery log screen (task [3.4], ws-6mkyj) — a searchable, filterable,
// paginated table built on the shared Table/Badge/Input/Select atoms. All
// filtering, searching, and paging operate IN MEMORY over the single `records`
// array fetched by useDeliveryLog(); there are zero additional network calls
// after the initial load (local useState drives search/status/page, plain array
// derivation produces the visible slice). The search box is debounced (280ms)
// and the visible rows stagger-fade in on every search/filter/page change —
// both are pure client-side timing/animation, no extra fetches.
import { useEffect, useMemo, useState } from 'react';
import {
	AlertCircle,
	ChevronLeft,
	ChevronRight,
	Inbox,
	MoreHorizontal,
	Search,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router';

import { useDeliveryLog } from '@/api/delivery-log/hooks';
import {
	DELIVERY_STATUSES,
	type DeliveryRecord,
	type DeliveryStatus,
} from '@/api/delivery-log/mock';
import { PageContainer } from '@/components/layout/page-container';
import { PageHeader } from '@/components/layout/page-header';
import { Badge, type BadgeStatus } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 280;
const ROW_STAGGER_MS = 30;

type StatusFilter = DeliveryStatus | 'all';

/**
 * Returns `value` delayed until it has been stable for `delayMs` — the filter
 * re-runs only after the user's LAST keystroke, not on every keystroke. Pure
 * timer, no network, no dependency.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const id = setTimeout(() => setDebounced(value), delayMs);
		return () => clearTimeout(id);
	}, [value, delayMs]);
	return debounced;
}

/** Domain delivery status → shared Badge status color. Styling lives in the atom. */
const STATUS_TO_BADGE: Record<DeliveryStatus, BadgeStatus> = {
	delivered: 'success',
	failed: 'danger',
	pending: 'neutral',
	bounced: 'warning',
};

function StatusChip({ status }: { status: DeliveryStatus }) {
	return <Badge status={STATUS_TO_BADGE[status]}>{status}</Badge>;
}

function formatTimestamp(iso: string): string {
	const d = new Date(iso);
	return d.toLocaleString(undefined, {
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	});
}

export default function DeliveryLogPage() {
	const navigate = useNavigate();
	// --- Local state (search/filter/page) — the only state this screen owns ---
	// `search` tracks the input on every keystroke (keeps the box responsive);
	// the FILTER reads `debouncedSearch`, which settles 280ms after typing stops.
	const [search, setSearch] = useState('');
	const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
	const [status, setStatus] = useState<StatusFilter>('all');
	const [page, setPage] = useState(1);

	// --- API: single fetch; everything below derives from its `records` array ---
	const { data, isLoading, isError, refetch } = useDeliveryLog();
	const records = data?.records;

	// --- In-memory derivation: filter -> search -> the current page slice ---
	const filtered = useMemo<DeliveryRecord[]>(() => {
		const q = debouncedSearch.trim().toLowerCase();
		return (records ?? []).filter((r) => {
			if (status !== 'all' && r.status !== status) return false;
			if (!q) return true;
			return (
				r.recipient.toLowerCase().includes(q) ||
				r.subject.toLowerCase().includes(q) ||
				r.provider.toLowerCase().includes(q)
			);
		});
	}, [records, debouncedSearch, status]);

	const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	// Clamp during render (no effect needed): a filter change that shrinks the
	// result set below the current page just shows the last valid page.
	const currentPage = Math.min(page, pageCount);
	const start = (currentPage - 1) * PAGE_SIZE;
	const visible = filtered.slice(start, start + PAGE_SIZE);

	// Re-key the visible rows on any search/filter/page change so the stagger
	// entrance re-fires each time the set changes (CSS animations run on mount).
	const rowGeneration = `${currentPage}|${debouncedSearch}|${status}`;

	// Reset to page 1 once a debounced search settles (not per keystroke).
	useEffect(() => {
		setPage(1);
	}, [debouncedSearch]);

	function onStatus(v: StatusFilter) {
		setStatus(v);
		setPage(1);
	}

	return (
		<PageContainer>
			<PageHeader
				title="Delivery Log"
				description="Every delivery attempt across all providers."
			/>

			{/* --- Controls: search + status filter --- */}
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
				<div className="relative flex-1">
					<Search
						className="pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground"
						aria-hidden
					/>
					<Input
						type="search"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search recipient, subject, provider…"
						aria-label="Search delivery log"
						className="bg-card pl-9"
					/>
				</div>
				<Select
					value={status}
					onValueChange={(v) => onStatus(v as StatusFilter)}
				>
					<SelectTrigger
						aria-label="Filter by status"
						className="bg-card sm:w-48"
					>
						<SelectValue placeholder="All statuses" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All statuses</SelectItem>
						{DELIVERY_STATUSES.map((s) => (
							<SelectItem key={s} value={s}>
								{s.charAt(0).toUpperCase() + s.slice(1)}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			{/* --- States: loading -> error -> empty -> data --- */}
			{isLoading ? (
				<DeliveryLogSkeleton />
			) : isError ? (
				<div className="flex flex-col items-center gap-4 py-16 text-center">
					<AlertCircle className="size-10 text-destructive" aria-hidden />
					<p className="text-sm text-muted-foreground">
						Couldn’t load the delivery log.
					</p>
					<button
						type="button"
						onClick={() => refetch()}
						data-slot="button"
						className="border border-border bg-secondary px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
					>
						Try again
					</button>
				</div>
			) : filtered.length === 0 ? (
				<div className="flex flex-col items-center gap-3 py-16 text-center">
					<Inbox className="size-10 text-muted-foreground" aria-hidden />
					<div>
						<h3 className="font-semibold text-foreground">
							No matching deliveries
						</h3>
						<p className="text-sm text-muted-foreground">
							Adjust your search or status filter.
						</p>
					</div>
				</div>
			) : (
				<>
					{/* Scoped stagger-entrance keyframe (tailwindcss-animate is not
					    wired into index.css). prefers-reduced-motion disables it. */}
					<style>{`
@keyframes delivery-row-in {
	from { opacity: 0; transform: translateY(6px); }
	to { opacity: 1; transform: none; }
}
@media (prefers-reduced-motion: reduce) {
	[data-delivery-row] { animation: none !important; }
}
`}</style>
					<div className="overflow-x-auto rounded-lg border border-border">
						<Table className="text-left">
							<TableHeader className="bg-muted/40">
								<TableRow className="text-xs uppercase tracking-wide text-muted-foreground hover:bg-transparent">
									<TableHead className="px-4 py-1.5">Recipient</TableHead>
									<TableHead className="px-4 py-1.5">Subject</TableHead>
									<TableHead className="px-4 py-1.5">Provider</TableHead>
									<TableHead className="px-4 py-1.5">Status</TableHead>
									<TableHead className="px-4 py-1.5">Time</TableHead>
									{/* Trailing kebab column (resend.md dataTable token) — no label. */}
									<TableHead className="w-10 px-4 py-1.5">
										<span className="sr-only">Actions</span>
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{visible.map((r, i) => (
									<TableRow
										key={`${rowGeneration}:${r.id}`}
										data-delivery-row
										onClick={() =>
											void navigate(`/delivery-log/${r.id}`, {
												viewTransition: true,
												state: { record: r },
											})
										}
										className="group cursor-pointer hover:bg-accent/40"
										style={{
											animationName: 'delivery-row-in',
											animationDuration: '260ms',
											animationTimingFunction:
												'cubic-bezier(0.22, 1, 0.36, 1)',
											animationFillMode: 'both',
											animationDelay: `${i * ROW_STAGGER_MS}ms`,
										}}
									>
										<TableCell className="px-4 py-1.5 font-mono text-xs text-foreground">
											{r.recipient}
										</TableCell>
										<TableCell className="max-w-xs truncate px-4 py-1.5 text-foreground">
											{r.subject}
										</TableCell>
										<TableCell className="px-4 py-1.5 text-muted-foreground">
											{r.provider}
										</TableCell>
										<TableCell className="px-4 py-1.5">
											<StatusChip status={r.status} />
										</TableCell>
										<TableCell
											className="whitespace-nowrap px-4 py-1.5 text-muted-foreground"
											suppressHydrationWarning
										>
											{formatTimestamp(r.timestamp)}
										</TableCell>
										{/* Trailing kebab trigger — hidden until row hover /
										    keyboard focus, opens the entry detail. Keeps a
										    focusable, accessible path to the same route the
										    whole-row click navigates to. */}
										<TableCell className="px-4 py-1.5 text-right">
											<Link
												to={`/delivery-log/${r.id}`}
												state={{ record: r }}
												viewTransition
												onClick={(e) => e.stopPropagation()}
												aria-label={`View delivery to ${r.recipient}`}
												className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
											>
												<MoreHorizontal className="size-4" aria-hidden />
											</Link>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>

					{/* --- Pagination --- */}
					<div className="flex items-center justify-between text-sm text-muted-foreground">
						<span>
							Showing {start + 1}–
							{Math.min(start + PAGE_SIZE, filtered.length)} of {filtered.length}
						</span>
						<div className="flex items-center gap-2">
							<button
								type="button"
								data-slot="button"
								onClick={() => setPage((p) => Math.max(1, p - 1))}
								disabled={currentPage <= 1}
								aria-label="Previous page"
								className="flex items-center gap-1 border border-border bg-secondary px-3 py-1.5 font-medium text-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
							>
								<ChevronLeft className="size-4" aria-hidden />
								Prev
							</button>
							<span className="tabular-nums">
								{currentPage} / {pageCount}
							</span>
							<button
								type="button"
								data-slot="button"
								onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
								disabled={currentPage >= pageCount}
								aria-label="Next page"
								className="flex items-center gap-1 border border-border bg-secondary px-3 py-1.5 font-medium text-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
							>
								Next
								<ChevronRight className="size-4" aria-hidden />
							</button>
						</div>
					</div>
				</>
			)}
		</PageContainer>
	);
}

function DeliveryLogSkeleton() {
	return (
		<div className="space-y-3">
			{Array.from({ length: PAGE_SIZE }).map((_, i) => (
				<div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
			))}
		</div>
	);
}
